// M85 Phase b (D20/D33) — the packing list, readable with no signal.
//
// ── WHY THIS EXISTS AT ALL ──────────────────────────────────────────────────
//
// The stated moment of use is standing over a bag in a garage with one bar.
// This app has no service worker (verified: no registration anywhere in src/,
// public/, index.html or netlify.toml), so the page does not load offline at
// all — and after Phase 0 it renders an honest error banner instead of an
// empty list, which is better and still unusable. Caching the rendered list
// turns "can't load" into "here's what you had".
//
// ── WHY draftStorage AND NOT tripTaps ───────────────────────────────────────
//
// `tripTaps.ts` is the obvious template — same page, same per-session keying —
// and it is the wrong one on three counts:
//
//   • It stores INTEGERS keyed by uuid. This stores firearm names, build
//     labels and ammo lot names, so it is disclosure-shaped where taps are not.
//   • It has no TTL and no quota kill-switch: its write swallows the throw and
//     keeps retrying forever.
//   • It is cleared at close-out. A cache of SERVER state needs staleness, not
//     a lifecycle event.
//
// `draftStorage.ts` already solved all three, so this mirrors it: a
// session-scoped kill switch set the first time a write throws, a TTL applied
// on read, and a stored timestamp so the UI can say the copy is not live.
//
// ── AND THE PART THAT IS NOT ABOUT THIS FEATURE ─────────────────────────────
//
// `AuthContext.tsx` calls `supabase.auth.signOut()` and nothing else — there is
// no `localStorage.clear()` and no prefix sweep anywhere in the app. Taps got
// away with that; inventory names do not. `clearAllPackCaches()` is exported
// for the sign-out path to call.

const PREFIX = 'qs.pack.';

/** A cached list is worth showing for a day; after that, ask the network. */
export const PACK_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export interface CachedPackList<T = unknown> {
  items: T[];
  /** ISO timestamp of the write. Drives both the TTL and the "last loaded" line. */
  savedAt: string;
}

// Session-scoped kill switch, set the first time storage throws and never
// reset: a quota-exhausted or private-mode browser will keep throwing, and one
// warning is enough.
let cacheDisabled = false;
let warned = false;

/** False once storage has failed this session. */
export function packCacheAvailable(): boolean {
  return !cacheDisabled;
}

/** Test seam: restore the module to a clean session. */
export function resetPackCacheAvailability(): void {
  cacheDisabled = false;
  warned = false;
}

function disable(err: unknown): void {
  cacheDisabled = true;
  if (!warned) {
    warned = true;
    console.warn('[packing] localStorage unavailable — the offline copy is off for this session.', err);
  }
}

export function packCacheKey(sessionId: string): string {
  return `${PREFIX}${sessionId}`;
}

/**
 * Read the cached list, or null.
 *
 * Returns null past the TTL and DELETES the entry, so a stale copy cannot come
 * back after a clock change or a long-abandoned trip.
 */
export function readPackCache<T = unknown>(sessionId: string, now: number = Date.now()): CachedPackList<T> | null {
  if (cacheDisabled) return null;
  try {
    const raw = localStorage.getItem(packCacheKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPackList<T>;
    // Defensive: anything could be under this key, including a half-written
    // value from a killed tab.
    if (!parsed || !Array.isArray(parsed.items) || typeof parsed.savedAt !== 'string') return null;
    const age = now - new Date(parsed.savedAt).getTime();
    if (!Number.isFinite(age) || age > PACK_CACHE_TTL_MS) {
      clearPackCache(sessionId);
      return null;
    }
    return parsed;
  } catch {
    // A malformed entry is not worth a kill switch — it is worth ignoring.
    return null;
  }
}

/** Returns false when the write did not happen. */
export function writePackCache(sessionId: string, items: unknown[], now: Date = new Date()): boolean {
  if (cacheDisabled) return false;
  try {
    localStorage.setItem(packCacheKey(sessionId), JSON.stringify({ items, savedAt: now.toISOString() }));
    return true;
  } catch (err) {
    disable(err);
    return false;
  }
}

export function clearPackCache(sessionId: string): void {
  try {
    localStorage.removeItem(packCacheKey(sessionId));
  } catch {
    /* nothing to do — the entry is unreachable either way */
  }
}

/**
 * Drop every cached list. Called on sign-out.
 *
 * Iterates a snapshot of the keys rather than the live index, because removing
 * while enumerating `localStorage` skips entries.
 */
export function clearAllPackCaches(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage unavailable — nothing cached to leak */
  }
}
